import usePermissions from "@/hooks/use-permissions.hook.ts";
import NotFound from "@/pages/NotFound.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { RouteConfig } from "@/routes/types/route.types.ts";

interface ProtectedRouteProps {
  config: RouteConfig;
}

export const ProtectedRoute = ({ config }: ProtectedRouteProps) => {
  const { data: employeeMe } = useEmployeeMe();
  const { canIView, canIEdit, canCurrentEmployeeEditField } = usePermissions();
  const { id } = useParams();

  const isAllowed = useMemo(() => {
    if (!employeeMe) return false;
    if (config.bypassSelfCheck && id !== undefined && String(employeeMe.id) === id) return true;

    const featureEnabled = config.planFeature
      ? employeeMe?.plan?.features?.[config.planFeature]?.enabled
      : true;
    if (!featureEnabled) return false;

    const hasAllRequiredPermissions =
      config.allOfPermissions && config.allOfPermissions.length > 0
        ? config.allOfPermissions.every((p) => canIEdit(p))
        : true;

    const hasAnyOfViewPermissions =
      config.anyOfViewPermissions && config.anyOfViewPermissions.length > 0
        ? config.anyOfViewPermissions.some((p) => canIView(p))
        : true;

    const anyOfPermissions = config.anyOfPermissions ?? [];
    const anyOfEditFieldPermissions = config.anyOfEditFieldPermissions ?? [];
    const hasAnyOfPermissions =
      anyOfPermissions.length > 0 || anyOfEditFieldPermissions.length > 0
        ? anyOfPermissions.some((p) => canIEdit(p)) ||
          anyOfEditFieldPermissions.some((p) => canCurrentEmployeeEditField(p))
        : true;

    const hasAllEditFieldPermissions =
      config.allOfEditFieldPermissions && config.allOfEditFieldPermissions.length > 0
        ? config.allOfEditFieldPermissions.every((p) => canCurrentEmployeeEditField(p))
        : true;

    return (
      hasAllRequiredPermissions &&
      hasAnyOfPermissions &&
      hasAnyOfViewPermissions &&
      hasAllEditFieldPermissions
    );
  }, [
    config.anyOfPermissions,
    config.allOfPermissions,
    config.allOfEditFieldPermissions,
    config.anyOfEditFieldPermissions,
    employeeMe,
    config.planFeature,
    config.bypassSelfCheck,
    id,
  ]);
  if (!isAllowed) {
    return <NotFound />;
  }

  return config.element || <Outlet />;
};
